import { SelectionDrawer } from '@openx/components-core/lib/SelectionDrawer/SelectionDrawer';
import type { ItemRenderProps, SelectedItemRenderProps } from '@openx/components-core/lib/SelectionDrawer/types';
import type { SegmentsOption } from '@openx/types/targeting/customVariables';
import { Fragment, useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useBulkAdd } from '../../shared/BulkAdd/useBulkAdd';
import { type EnrichedProviderSegments, getContextualSegmentKey } from '../utils/enrichment';
import { ContextualSegmentsBulkDrawer } from './ContextualSegmentsBulkDrawer';
import { ContextualSegmentListItem } from './components/ContextualSegmentListItem';
import { ContextualSegmentsSearchAndFilters } from './components/ContextualSegmentsSearchAndFilters';
import { type GroupedSegmentItem, SelectedSegmentsWithSections } from './components/SelectedSegmentsWithSections';
import { useContextualSegmentsDrawerAdapter } from './hooks/useContextualSegmentsDrawerAdapter';
import { createGroupedSegmentItems } from './utils/createGroupedSegmentItems';

type ContextualSegmentsSelectionDrawerProps = {
  isOpen: boolean;
  onClose: () => void;
  onApply: (data: EnrichedProviderSegments[]) => void;
  enrichedSegments: EnrichedProviderSegments[];
  accountUid: string;
  readonly?: boolean;
};

export const ContextualSegmentsSelectionDrawer = ({
  isOpen,
  onClose,
  onApply,
  enrichedSegments,
  accountUid,
  readonly = false,
}: ContextualSegmentsSelectionDrawerProps) => {
  const [showDiscardModal, setShowDiscardModal] = useState(false);
  const { t } = useTranslation();
  const {
    availableSegments,
    totalCount,
    selectedIds,
    selectedIdsMap,
    getAvailableSegmentKey,
    isSegmentProviderResolved,
    onItemInclude,
    onItemExclude,
    selectedIncludes,
    selectedExcludes,
    onItemRemove,
    onRemoveAll,
    loading,
    handleApply,
    handleDiscard,
    hasChanges,
    pagination,
    hasSingleAccountProvider,
    hasOwnSegments,
    isBulkAddDisabled,
    validateBulkSegments,
    addBulkSegments,
    segmentFilters,
    dataPartnerOptions,
    dataPartnerOptionsLoading,
  } = useContextualSegmentsDrawerAdapter({ enrichedSegments, accountUid, onClose, onApply });

  const { isBulkOpen, onBulkAdd, onBulkClose, onBulkOpen } = useBulkAdd({
    onAdd: (data: { serializedSegments: string[]; inclusionType: 'includes' | 'excludes' }) => {
      addBulkSegments(data.serializedSegments, data.inclusionType);
    },
  });

  const groupedSelectedItems = useMemo(
    () =>
      createGroupedSegmentItems(
        selectedIncludes.map(item => ({ ...item, readonly })),
        selectedExcludes.map(item => ({ ...item, readonly }))
      ),
    [readonly, selectedIncludes, selectedExcludes]
  );
  const selectedSegmentsCount = selectedIncludes.length + selectedExcludes.length;
  const showBulkAdd = hasSingleAccountProvider && !readonly;
  const leftPanelHeader = (
    <ContextualSegmentsSearchAndFilters
      phrase={segmentFilters.phrase}
      onPhraseChange={segmentFilters.setPhrase}
      source={segmentFilters.source}
      onSourceChange={segmentFilters.setSource}
      filters={segmentFilters.filters}
      onFiltersChange={segmentFilters.setFilters}
      dataPartnerOptions={dataPartnerOptions}
      dataPartnerOptionsLoading={dataPartnerOptionsLoading}
      hasOwnSegments={hasOwnSegments}
      hasActiveFilters={segmentFilters.hasActiveFilters}
      onClearAll={segmentFilters.clearAll}
      showBulkAdd={showBulkAdd}
      isBulkAddDisabled={isBulkAddDisabled}
      onBulkAdd={onBulkOpen}
    />
  );
  const handleClose = useCallback(() => {
    if (hasChanges) setShowDiscardModal(true);
    else handleDiscard();
  }, [hasChanges, handleDiscard]);

  const renderItem = useCallback(
    (props: ItemRenderProps<SegmentsOption>) => {
      return (
        <ContextualSegmentListItem
          {...props}
          selectionType={selectedIdsMap.get(getAvailableSegmentKey(props.item))}
          onInclude={onItemInclude}
          onExclude={onItemExclude}
          isProviderResolved={isSegmentProviderResolved(props.item)}
          readonly={readonly}
          searchPhrase={segmentFilters.phrase}
        />
      );
    },
    [
      getAvailableSegmentKey,
      isSegmentProviderResolved,
      onItemExclude,
      onItemInclude,
      readonly,
      segmentFilters.phrase,
      selectedIdsMap,
    ]
  );

  const renderSelectedItem = useCallback((props: SelectedItemRenderProps<GroupedSegmentItem>) => {
    return <SelectedSegmentsWithSections {...props} />;
  }, []);

  return (
    <Fragment>
      <SelectionDrawer<SegmentsOption, GroupedSegmentItem>
        dataTest="audience-segment-drawer"
        title={t('Contextual Segments')}
        open={isOpen}
        renderItem={renderItem}
        renderSelectedItem={renderSelectedItem}
        leftPanel={{
          title: t('Segments List'),
          items: availableSegments,
          totalCount,
          selectedIds,
          onItemSelect: () => {
            // Items expose separate Include and Exclude actions.
          },
          getItemId: getAvailableSegmentKey,
          emptyStateMessage: t('No segments available'),
          loading,
          pagination,
          pendoFeature: 'contextual-segments-list',
        }}
        rightPanel={{
          title: selectedSegmentsCount === 0 ? `${t('Segments')} [ 0 ]` : t('Segments'),
          items: groupedSelectedItems,
          selectedCount: selectedSegmentsCount,
          onItemRemove: item => {
            if (!readonly && item.type === 'segment') onItemRemove(item);
          },
          getItemId: item =>
            item.type === 'segment'
              ? getContextualSegmentKey({ providerId: item.providerId, segmentId: item.segment.id })
              : item.id,
          onRemoveAll: readonly ? undefined : onRemoveAll,
          emptyStateMessage: t('No segments selected'),
          pendoFeature: 'contextual-segments-selected',
        }}
        actions={{
          onApply: handleApply,
          onClose: handleClose,
          applyLabel: t('Apply'),
          applyDisabled: !hasChanges || readonly,
          applyPendoFeature: 'contextual-segments-apply',
        }}
        discardConfig={
          showDiscardModal
            ? {
                show: true,
                onConfirm: () => {
                  setShowDiscardModal(false);
                  handleDiscard();
                },
                onCancel: () => setShowDiscardModal(false),
                title: t('Discard changes?'),
                message: t('You have unsaved changes. Are you sure you want to close without saving?'),
              }
            : undefined
        }
        slots={{ leftPanelHeader }}
        drawerSx={{
          '& .MuiDrawer-paper': {
            height: '100vh',
            maxHeight: '100vh',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
            padding: 0,
            width: { xs: '90vw', md: '800px', lg: '900px', xl: '1000px' },
            maxWidth: '90vw',
          },
        }}
        data-test="contextual-segments-selection-drawer"
      />

      {isBulkOpen ? (
        <ContextualSegmentsBulkDrawer
          onClose={onBulkClose}
          onApply={onBulkAdd}
          validateBulkSegments={validateBulkSegments}
        />
      ) : null}
    </Fragment>
  );
};
